import toast from 'react-hot-toast';
import { downloadAnalyticsExport } from './api/analyticsApi';

/**
 * Runs an analytics export in the background.
 *
 * Christian, 11 Sep 2026: "to avoid waiting for the html to build, is there any
 * chance that this will trigger in the back while the user is scrolling".
 *
 * A wide export is a minute of work no matter where it runs, so the fix is not
 * to make the user watch it. The dialog closes the moment Export is pressed and
 * the request carries on here, OUTSIDE any component: this module is not
 * unmounted when the dialog closes or when the user moves to another page, so
 * the download still arrives. A toast holds the place until it does.
 *
 * The one thing that ends it is a full page reload, which is also the one thing
 * the toast asks the user not to do.
 */

/** Exports currently running, so pressing Export twice does not run it twice. */
const running = new Map();

export const exportKey = ({ scope, startDate, endDate, fileFormat, datasets }) =>
    [scope, startDate, endDate, fileFormat, (datasets || []).join('+')].join('|');

export const isExportRunning = (options) => running.has(exportKey(options));

/**
 * Forget what is in flight. For tests only: this module deliberately keeps its
 * state outside React, so a test that leaves an export hanging would otherwise
 * make the next one look like a duplicate.
 */
export const clearRunningExports = () => running.clear();

/** Hands the finished file to the browser. */
const saveBlob = (response, filename) => {
    const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/octet-stream',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Start an export. Resolves when the file has downloaded; the caller does not
 * have to wait for it, and nothing about the result depends on it staying
 * mounted.
 *
 * @param {Object} options
 * @param {'portal'|'crm'|'executive'} options.scope
 * @param {string} options.startDate YYYY-MM-DD
 * @param {string} options.endDate   YYYY-MM-DD
 * @param {string[]} [options.datasets] dataset keys; empty for everything
 * @param {'xlsx'|'csv'|'html'} [options.fileFormat]
 * @param {string} [options.label] what to call it in the toast
 * @returns {Promise<boolean>} true if the file downloaded
 */
export const startAnalyticsExport = async (options) => {
    const {
        scope, startDate, endDate, datasets = [], fileFormat = 'xlsx',
        label = 'analytics',
    } = options;

    const key = exportKey({ scope, startDate, endDate, fileFormat, datasets });
    if (running.has(key)) {
        toast('That export is already running. It will download when it is ready.',
            { id: running.get(key) });
        return false;
    }

    const toastId = toast.loading(
        `Preparing your ${label} export. Keep working, it will download when it is ready.`,
        { duration: Infinity }
    );
    running.set(key, toastId);

    try {
        const response = await downloadAnalyticsExport({
            scope, startDate, endDate, datasets, fileFormat,
        });
        const name = label.toLowerCase().replace(/\s+/g, '-');
        saveBlob(response, `${name}_${startDate}_to_${endDate}.${fileFormat}`);
        toast.success('Your export has downloaded', { id: toastId, duration: 6000 });
        return true;
    } catch (err) {
        console.error('Analytics export failed:', err);
        toast.error(
            err?.response?.status === 403
                ? 'You do not have access to that export'
                : 'Export failed. Try fewer tabs or a shorter date range.',
            { id: toastId, duration: 8000 }
        );
        return false;
    } finally {
        running.delete(key);
    }
};

export default startAnalyticsExport;
